import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { forkJoin } from 'rxjs';
import { forEach, isEmpty, without, find, cloneDeep } from 'lodash';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import selectStyles from '../../../util/selectStyles';
import TagModel from '../../Tags/TagModel';
import ChatbotOptionModel from '../ChatbotOptionModel';
import RetailerUserModel from '../../RetailerUsers/RetailerUserModel';
import CtxServiceModel from '../../CtxService/CtxServiceModel';
import FunnelModel from '../../Funnels/FunnelModel';
import WhatsappFlowModelMC from '../../../presentation/models/WhatsappFlowModelMC';
import ButtonMC from '../../../presentation/components/ButtonMC';
import IconMC from '../../../presentation/components/IconMC';
import {
  CHATBOT_OPTIONS_TYPE,
  CHATBOT_OPTION_ACTIONS_TYPE,
  CHATBOT_ACTION_SCHEDULE_TYPES,
  WEEK_DAYS_OPTIONS,
  CUSTOMER_PROTECTED_AUTOMATION_TARGETS
} from '../../../constants/AppConstants';
import { getOptionCollection } from './utils';
import { isMicroserviceBotOrRetailer } from '../../../services/chatbots/chatbotEngineRouting';
import { microserviceChatbotsCache } from '../../../services/chatbots/dualApiMergeChatbotService';

const AdvancedActions = ({
  chatbotOption,
  setChatbotOption,
  customerColumns,
  errors = {},
  chatbot
}) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const { chatbots, chatbotSaved } = useSelector((reduxState) => reduxState.chatbotsReducer || {});
  const currentChatbot = chatbot || chatbots?.find((c) => c.web_id === id) || chatbotSaved;

  const isMicroservice = useMemo(() => {
    if (id && microserviceChatbotsCache.has(id)) return true;
    return isMicroserviceBotOrRetailer(currentChatbot, retailer_info);
  }, [currentChatbot, retailer_info, id]);

  const allowScheduleConfig = !isMicroservice;

  const scheduleTypeOptions = useMemo(() => [
    { value: 'always', label: t('chatbot.config.actions.schedule.types.always', { defaultValue: 'Siempre (24/7)' }) },
    { value: 'custom_schedule', label: t('chatbot.config.actions.schedule.types.custom_schedule', { defaultValue: 'En horario específico' }) },
    { value: 'out_of_schedule', label: t('chatbot.config.actions.schedule.types.out_of_schedule', { defaultValue: 'Por defecto (fuera de horario)' }) }
  ], [t]);

  const weekDaysOptions = useMemo(() => [
    { value: 1, label: t('chatbot.config.actions.schedule.days.short.1', { defaultValue: 'L' }), fullLabel: t('chatbot.config.actions.schedule.days.full.1', { defaultValue: 'Lunes' }) },
    { value: 2, label: t('chatbot.config.actions.schedule.days.short.2', { defaultValue: 'M' }), fullLabel: t('chatbot.config.actions.schedule.days.full.2', { defaultValue: 'Martes' }) },
    { value: 3, label: t('chatbot.config.actions.schedule.days.short.3', { defaultValue: 'X' }), fullLabel: t('chatbot.config.actions.schedule.days.full.3', { defaultValue: 'Miércoles' }) },
    { value: 4, label: t('chatbot.config.actions.schedule.days.short.4', { defaultValue: 'J' }), fullLabel: t('chatbot.config.actions.schedule.days.full.4', { defaultValue: 'Jueves' }) },
    { value: 5, label: t('chatbot.config.actions.schedule.days.short.5', { defaultValue: 'V' }), fullLabel: t('chatbot.config.actions.schedule.days.full.5', { defaultValue: 'Viernes' }) },
    { value: 6, label: t('chatbot.config.actions.schedule.days.short.6', { defaultValue: 'S' }), fullLabel: t('chatbot.config.actions.schedule.days.full.6', { defaultValue: 'Sábado' }) },
    { value: 7, label: t('chatbot.config.actions.schedule.days.short.7', { defaultValue: 'D' }), fullLabel: t('chatbot.config.actions.schedule.days.full.7', { defaultValue: 'Domingo' }) }
  ], [t]);

  const initiateActionsOption = () => {
    let actions = CHATBOT_OPTION_ACTIONS_TYPE;

    if (!retailer_info.ctx_context) {
      actions = actions.filter((el) => el.value !== 'ctx_notify');
    }

    return actions.map((item) => ({ ...item, isDisabled: false }));
  };

  const [actionTypeOptions, setActionTypeOptions] = useState(initiateActionsOption());
  const [tagOptions, setTagOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [customerFields, setCustomerFields] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [funnelOptions, setFunnelOptions] = useState([]);
  const [funnelStepOptions, setFunnelStepOptions] = useState([]);
  const [flowOptions, setFlowOptions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [trackers, setTrackers] = useState([]);

  const chatBotActions = useMemo(
    () => getOptionCollection(chatbotOption, 'chat_bot_actions'),
    [chatbotOption.chat_bot_actions]
  );

  const enabledChatBotActions = chatBotActions.filter((el) => el._destroy !== true);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const options = customerColumns
      .filter((field) => !CUSTOMER_PROTECTED_AUTOMATION_TARGETS.includes(field?.[1]))
      .map((field) => ({
        value: field[1],
        label: <span className="fz-14">{field[0]}</span>
      }));

    setCustomerFields(options);
  }, [customerColumns]);

  useEffect(() => {
    if (isEmpty(tagOptions)) return;

    let tagIn;

    forEach(chatBotActions, (action) => {
      // eslint-disable-next-line no-param-reassign
      action.selected_tags = [];

      forEach(action.tags, (tag) => {
        tagIn = find(tagOptions, { value: tag.id });
        if (tagIn) action.selected_tags.push(tagIn);
      });
    });

    updateInfo('chat_bot_actions', cloneDeep(chatBotActions));
  }, [tagOptions]);

  useEffect(() => {
    if (isEmpty(funnelOptions)) return;

    const action = chatBotActions.find((el) => el.action_type === 'create_deal');

    if (!isEmpty(action)) {
      const funnel = funnelOptions.find((el) => el.value === action.funnel_id);

      if (!isEmpty(funnel)) {
        setFunnelStepOptions(
          funnel.funnelSteps.map((el) => ({
            value: el.id,
            label: el.name,
            web_id: el.web_id
          }))
        );
      }
    }
  }, [funnelOptions]);

  useEffect(() => {
    setActionTypeOptions(initiateActionsOption());
  }, [retailer_info.ctx_context]);

  useEffect(() => {
    if (allowScheduleConfig) return;

    const newOptions = actionTypeOptions.map((opt) => {
      const selectedOpt = find(enabledChatBotActions, {
        action_type: opt.value
      });
      return { ...opt, isDisabled: Boolean(selectedOpt) };
    });

    setActionTypeOptions(newOptions);
  }, [chatBotActions, allowScheduleConfig]);

  useEffect(() => {
    if (selectedGoal == null) return;

    setTrackers(goals[selectedGoal].trackers);
  }, [selectedGoal]);

  const loadInitialData = () => {
    const promises = [
      TagModel.get(),
      RetailerUserModel.getAgentsByRole(),
      ChatbotOptionModel.getTeamAssignments(id),
      CtxServiceModel.getGoals(),
      FunnelModel.get(),
      WhatsappFlowModelMC.get({ status: 'PUBLISHED' })
    ];
    forkJoin(promises).subscribe((results) => {
      setTagOptions(results[0].tags);

      setAgentOptions(
        results[1].agents.map((agent) => ({
          value: agent.id,
          label: `${agent.first_name} ${agent.last_name}`
        }))
      );

      setTeamOptions(
        results[2].team_assignments.map((team) => ({
          value: team[1],
          label: team[0]
        }))
      );

      setGoals(results[3].goals);

      setFunnelOptions(
        results[4].funnels.map((funnel) => ({
          value: funnel.id,
          label: funnel.name,
          web_id: funnel.web_id,
          funnelSteps: funnel.funnel_steps,
          funnel_name: funnel.name
        }))
      );

      setFlowOptions(
        (results[5]?.whatsapp_flows || []).map((flow) => ({
          value: flow.id,
          label: flow.name
        }))
      );

      setSelectedGoal(getGoalIndex());
    });
  };

  const updateInfo = (name, value) => {
    setChatbotOption((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleInputChange = (e) => {
    const { target } = e;
    const { name } = target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    if (name === 'has_step_actions' && value === true && isEmpty(chatBotActions)) {
      addNewAction();
    }

    updateInfo(name, value);
  };

  const handleSelectAction = (option, action) => {
    const newArray = [...chatBotActions];
    const actionIndex = newArray.lastIndexOf(action);

    if (actionIndex !== -1) newArray[actionIndex].action_type = option.value;

    updateInfo('chat_bot_actions', newArray);
  };

  const addNewAction = () => {
    const newArray = [
      ...chatBotActions,
      {
        id: null,
        action_type: null,
        selected_tags: [],
        retailer_user_id: null,
        target_field: null,
        exit_message: '',
        team_assignment_id: null,
        funnel_id: null,
        funnel_step_id: null,
        goal_index: null,
        tracker: null,
        tracker_value: null,
        schedule_type: 'always',
        schedule_weekdays: [1, 2, 3, 4, 5],
        schedule_start_time: '08:00',
        schedule_end_time: '17:00',
        _destroy: false
      }
    ];

    updateInfo('chat_bot_actions', newArray);
  };

  const removeAction = (action) => {
    let newArray = [...chatBotActions];

    if (!action.id) {
      newArray = without(newArray, action);
    } else {
      const actionIndex = newArray.lastIndexOf(action);
      newArray[actionIndex]._destroy = true;
    }

    updateInfo('chat_bot_actions', newArray);
  };

  const updateAction = (action, name, value) => {
    const newArray = [...chatBotActions];
    const actionIndex = newArray.lastIndexOf(action);

    if (actionIndex !== -1) {
      newArray[actionIndex][name] = value;

      if (name === 'goal_index') {
        newArray[actionIndex].tracker = '';
        newArray[actionIndex].tracker_value = '';
      }
    }

    if (name === 'goal_index') {
      setSelectedGoal(value);
    }

    updateInfo('chat_bot_actions', newArray);
  };

  const handleSelect = (action) => ({ value }, { name }) => {
    updateAction(action, name, value);
  };

  const handleInputChangeArray = (action) => (e) => {
    updateAction(action, e.target.name, e.target.value);
  };

  const handleTagSelect = (options, action) => {
    updateAction(action, 'selected_tags', options);
  };

  const handleSelectFunnel = (action) => ({ value }, { name }) => {
    const newArray = [...chatBotActions];
    const actionIndex = newArray.lastIndexOf(action);
    if (actionIndex !== -1) {
      newArray[actionIndex][name] = value;
      newArray[actionIndex].funnel_step_id = null;
    }
    updateInfo('chat_bot_actions', newArray);
  };

  const canAddAction = () => {
    if (!allowScheduleConfig) {
      return Boolean(find(actionTypeOptions, { isDisabled: false }));
    }
    return enabledChatBotActions.every((a) => !!a.action_type);
  };

  const getGoalIndex = () => {
    const action = chatBotActions.find((el) => el.action_type === 'ctx_notify');

    return action?.goal_index;
  };

  // eslint-disable-next-line consistent-return
  const renderActionValue = (action) => {
    if (action.action_type === 'add_tag') {
      return (
        <div className="mt-5 col-md-11">
          <div className="figma-field mb-0">
            <label>Etiquetas</label>
            <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.selected_tags_error}</span>
            <Select
              isMulti
              isSearchable
              menuPortalTarget={document.body}
              menuShouldBlockScroll={false}
              captureMenuScroll={false}
              placeholder="Seleccionar"
              className={isEmpty(action.selected_tags) ? 'select-tag' : ''}
              classNamePrefix={isEmpty(action.selected_tags) ? 'select-tag' : ''}
              noOptionsMessage={() => 'Etiqueta no encontrado'}
              onChange={(options) => handleTagSelect(options, action)}
              styles={selectStyles}
              options={tagOptions}
              isOptionDisabled={() => action.selected_tags.length >= 5}
              name="selected_tags"
              value={action.selected_tags}
              components={{
                IndicatorSeparator: () => null
              }}
            />
          </div>
        </div>
      );
    }

    if (action.action_type === 'assign_agent') {
      return (
        <div className="mt-5 col-md-11">
          <div className="figma-field mb-0">
            <label>Agente</label>
            <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.retailer_user_id_error}</span>
            <Select
              options={agentOptions}
              placeholder="Selecciona un agente"
              className="select-tag"
              classNamePrefix="select-tag"
              styles={selectStyles}
              onChange={handleSelect(action)}
              value={[agentOptions.find((el) => el.value === action.retailer_user_id)]}
              name="retailer_user_id"
              components={{
                IndicatorSeparator: () => null
              }}
            />
          </div>
        </div>
      );
    }

    if (action.action_type === 'get_out_bot') {
      return (
        <div className="mt-5 col-md-11">
          <div className="figma-field mb-0">
            <label>Mensaje enviado al salir</label>
            <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.exit_message_error}</span>
            <textarea
              value={action.exit_message}
              onChange={handleInputChangeArray(action)}
              className="mercately-input mercately-input-textarea"
              name="exit_message"
            />
          </div>
        </div>
      );
    }

    if (action.action_type === 'save_on_db') {
      return (
        <>
          <div className="mt-5 col-md-11">
            <div className="figma-field mb-0">
              <label>Campo a guardar</label>
              <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.target_field_error}</span>
              <Select
                options={customerFields}
                placeholder="Selecciona un campo"
                className="select-tag"
                classNamePrefix="select-tag"
                styles={selectStyles}
                onChange={handleSelect(action)}
                value={[customerFields.find((el) => el.value === action.target_field)]}
                name="target_field"
                components={{
                  IndicatorSeparator: () => null
                }}
              />
            </div>
          </div>

          {chatbotOption.option_type === CHATBOT_OPTIONS_TYPE.QUESTION && (
            <div className="col-md-12 d-flex justify-content-between mt-20">
              <div>
                <span className="fz-14 text-gray-dark">
                  ¿Saltar este paso si ya tiene la información guardada?
                </span>
                <span
                  className="tw-ml-2 tw-inline-flex tw-align-middle"
                  data-tooltip-id="info-skip-step"
                >
                  <IconMC
                    name="question-mark-circle-outline"
                    size="base"
                    className="tw-text-gray-500"
                  />
                </span>
                <ReactTooltip id="info-skip-step" place="top" effect="solid">
                  <center>
                    <span>Si el campo seleccionado ya</span>
                    <br />
                    <span>tiene información almacenada,</span>
                    <br />
                    <span>no se ejecutará éste paso.</span>
                  </center>
                </ReactTooltip>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  name="skip_option"
                  checked={chatbotOption.skip_option}
                  onChange={handleInputChange}
                />
                <span className="slider round" />
              </label>
            </div>
          )}
        </>
      );
    }

    if (action.action_type === 'assign_team') {
      return (
        <div className="mt-5 col-md-11">
          <div className="figma-field mb-0">
            <label>Equipo</label>
            <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.team_assignment_id_error}</span>
            <Select
              options={teamOptions}
              placeholder="Selecciona un equipo"
              className="select-tag"
              classNamePrefix="select-tag"
              styles={selectStyles}
              onChange={handleSelect(action)}
              value={[teamOptions.find((el) => el.value === action.team_assignment_id)]}
              name="team_assignment_id"
              components={{
                IndicatorSeparator: () => null
              }}
            />
          </div>
        </div>
      );
    }

    if (action.action_type === 'ctx_notify') {
      return (
        <>
          <div className="mt-5 col-md-11">
            <div className="figma-field mb-0">
              <label>Objetivo</label>
              <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.goal_index_error}</span>
              <Select
                options={goals}
                placeholder="Selecciona un objetivo"
                className="select-tag"
                classNamePrefix="select-tag"
                styles={selectStyles}
                onChange={handleSelect(action)}
                value={[goals.find((el) => el.value === action.goal_index)]}
                name="goal_index"
                components={{
                  IndicatorSeparator: () => null
                }}
              />
            </div>
          </div>

          {selectedGoal != null && (
            <>
              <div className="mt-5 col-md-11">
                <div className="figma-field mb-0">
                  <label>Tracker</label>
                  <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.tracker_error}</span>
                  <Select
                    options={trackers}
                    placeholder="Selecciona un tracker"
                    className="select-tag"
                    classNamePrefix="select-tag"
                    styles={selectStyles}
                    onChange={handleSelect(action)}
                    value={[trackers.find((el) => el.value === action.tracker)]}
                    name="tracker"
                    components={{
                      IndicatorSeparator: () => null
                    }}
                  />
                </div>
              </div>

              <div className="mt-5 col-md-11">
                <div className="figma-field mb-0">
                  <label>Valor</label>
                  <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.tracker_value_error}</span>
                  <input
                    value={action.tracker_value}
                    onChange={handleInputChangeArray(action)}
                    className="mercately-input"
                    name="tracker_value"
                  />
                </div>
              </div>
            </>
          )}
        </>
      );
    }

    if (action.action_type === 'create_deal') {
      const selectedFunnel = funnelOptions.find((el) => el.value === action.funnel_id);
      const stepOptions = (selectedFunnel?.funnelSteps || []).map((el) => ({
        value: el.id,
        label: el.name,
        web_id: el.web_id
      }));

      return (
        <>
          <div className="mt-5 col-md-11">
            <div className="figma-field mb-0">
              <label>Embudos</label>
              <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.funnel_id_error}</span>
              <Select
                options={funnelOptions}
                placeholder="Selecciona Embudo"
                className="select-tag"
                classNamePrefix="select-tag"
                styles={selectStyles}
                onChange={handleSelectFunnel(action)}
                value={[funnelOptions.find((el) => el.value === action.funnel_id)]}
                name="funnel_id"
                components={{
                  IndicatorSeparator: () => null
                }}
              />
            </div>
          </div>

          {stepOptions.length > 0 && (
            <div className="mt-5 col-md-11">
              <div className="figma-field mb-0">
                <label>Etapa</label>
                <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.funnel_step_id_error}</span>
                <Select
                  options={stepOptions}
                  placeholder="Selecciona Etapa"
                  className="select-tag"
                  classNamePrefix="select-tag"
                  styles={selectStyles}
                  onChange={handleSelect(action)}
                  value={[stepOptions.find((el) => el.value === action.funnel_step_id)]}
                  name="funnel_step_id"
                  components={{
                    IndicatorSeparator: () => null
                  }}
                />
              </div>
            </div>
          )}
        </>
      );
    }

    if (action.action_type === 'send_whatsapp_flow') {
      return (
        <div className="mt-5 col-md-11">
          <div className="figma-field mb-0">
            <label>WhatsApp Flow</label>
            <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.whatsapp_flow_id_error}</span>
            <Select
              options={flowOptions}
              placeholder="Selecciona un WhatsApp Flow"
              className="select-tag"
              classNamePrefix="select-tag"
              styles={selectStyles}
              onChange={handleSelect(action)}
              value={[flowOptions.find((el) => el.value === action.whatsapp_flow_id)]}
              name="whatsapp_flow_id"
              components={{
                IndicatorSeparator: () => null
              }}
            />
          </div>
        </div>
      );
    }
  };

  const handleSelectScheduleType = (option, action) => {
    const newArray = [...chatBotActions];
    const actionIndex = newArray.lastIndexOf(action);
    if (actionIndex !== -1) {
      newArray[actionIndex].schedule_type = option.value;
      if (option.value === 'custom_schedule') {
        const existingDays = newArray[actionIndex].schedule_weekdays;
        if (!Array.isArray(existingDays) || existingDays.length === 0) {
          newArray[actionIndex].schedule_weekdays = [1, 2, 3, 4, 5];
        }
        if (!newArray[actionIndex].schedule_start_time) {
          newArray[actionIndex].schedule_start_time = '08:00';
        }
        if (!newArray[actionIndex].schedule_end_time) {
          newArray[actionIndex].schedule_end_time = '17:00';
        }
      } else {
        newArray[actionIndex].schedule_weekdays = [];
        newArray[actionIndex].schedule_start_time = null;
        newArray[actionIndex].schedule_end_time = null;
      }
    }
    updateInfo('chat_bot_actions', newArray);
  };

  const handleToggleWeekday = (dayValue, action) => {
    const newArray = [...chatBotActions];
    const actionIndex = newArray.lastIndexOf(action);
    if (actionIndex === -1) return;

    const currentDays = (newArray[actionIndex].schedule_weekdays || []).map(Number);
    let updatedDays;
    if (currentDays.includes(dayValue)) {
      updatedDays = currentDays.filter((d) => d !== dayValue);
    } else {
      updatedDays = [...currentDays, dayValue].sort((a, b) => a - b);
    }
    newArray[actionIndex].schedule_weekdays = updatedDays;
    updateInfo('chat_bot_actions', newArray);
  };

  const handleSetWeekdaysPreset = (preset, action) => {
    const newArray = [...chatBotActions];
    const actionIndex = newArray.lastIndexOf(action);
    if (actionIndex === -1) return;

    let days = [];
    if (preset === 'workdays') days = [1, 2, 3, 4, 5];
    else if (preset === 'weekend') days = [6, 7];
    else if (preset === 'all') days = [1, 2, 3, 4, 5, 6, 7];

    newArray[actionIndex].schedule_weekdays = days;
    updateInfo('chat_bot_actions', newArray);
  };

  const handleTimeChange = (fieldName, value, action) => {
    updateAction(action, fieldName, value);
  };

  const renderScheduleSettings = (action) => {
    const currentScheduleType = action.schedule_type || 'always';
    const weekdays = (action.schedule_weekdays || []).map(Number);
    const selectedScheduleOption = scheduleTypeOptions.find(
      (el) => el.value === currentScheduleType
    ) || scheduleTypeOptions[0];

    return (
      <div className="col-md-11 mt-10">
        <div className="figma-field mb-0">
          <label>{t('chatbot.config.actions.schedule.label', { defaultValue: 'Horario de ejecución' })}</label>
          <Select
            options={scheduleTypeOptions}
            placeholder={t('chatbot.config.actions.schedule.placeholder', { defaultValue: 'Selecciona horario' })}
            className="select-tag"
            classNamePrefix="select-tag"
            styles={selectStyles}
            onChange={(opt) => handleSelectScheduleType(opt, action)}
            value={[selectedScheduleOption]}
            name="schedule_type"
            components={{
              IndicatorSeparator: () => null
            }}
          />
        </div>

        {currentScheduleType === 'out_of_schedule' && (
          <div className="mt-10 p-10 tw-bg-gray-50 tw-rounded tw-border tw-border-gray-200">
            <span className="fz-12 tw-text-gray-600">
              {t('chatbot.config.actions.schedule.fallback_info', {
                defaultValue: 'Esta acción se ejecutará automáticamente como respaldo si el mensaje llega fuera de los horarios configurados en las otras acciones de este paso.'
              })}
            </span>
          </div>
        )}

        {currentScheduleType === 'custom_schedule' && (
          <div className="mt-10 p-15 tw-bg-gray-50 tw-rounded tw-border tw-border-gray-200">
            <div className="d-flex justify-content-between align-items-center mb-10">
              <span className="fz-12 fw-600 tw-text-gray-700">
                {t('chatbot.config.actions.schedule.applicable_days', { defaultValue: 'Días aplicables' })}
              </span>
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 fz-11 tw-no-underline tw-cursor-pointer"
                  onClick={() => handleSetWeekdaysPreset('workdays', action)}
                >
                  {t('chatbot.config.actions.schedule.presets.workdays', { defaultValue: 'Lun-Vie' })}
                </button>
                <span className="mx-5 fz-11 tw-text-gray-400">|</span>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 fz-11 tw-no-underline tw-cursor-pointer"
                  onClick={() => handleSetWeekdaysPreset('weekend', action)}
                >
                  {t('chatbot.config.actions.schedule.presets.weekend', { defaultValue: 'Fines de semana' })}
                </button>
                <span className="mx-5 fz-11 tw-text-gray-400">|</span>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 fz-11 tw-no-underline tw-cursor-pointer"
                  onClick={() => handleSetWeekdaysPreset('all', action)}
                >
                  {t('chatbot.config.actions.schedule.presets.all', { defaultValue: 'Todos' })}
                </button>
              </div>
            </div>

            <div className="d-flex mb-15 tw-gap-1.5">
              {weekDaysOptions.map((day) => {
                const isSelected = weekdays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    title={day.fullLabel}
                    onClick={() => handleToggleWeekday(day.value, action)}
                    className={`tw-w-8 tw-h-8 tw-rounded-full tw-text-xs tw-font-semibold tw-flex tw-items-center tw-justify-center tw-cursor-pointer ${
                      isSelected
                        ? 'tw-border-0 tw-bg-blue-600 tw-text-white'
                        : 'tw-border tw-border-gray-300 tw-bg-white tw-text-gray-600'
                    }`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>

            <div className="row">
              <div className="col-6">
                <div className="figma-field mb-0">
                  <label className="fz-12">
                    {t('chatbot.config.actions.schedule.start_time', { defaultValue: 'Hora inicio' })}
                  </label>
                  <input
                    type="time"
                    className="mercately-input"
                    value={action.schedule_start_time || '08:00'}
                    onChange={(e) => handleTimeChange('schedule_start_time', e.target.value, action)}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="figma-field mb-0">
                  <label className="fz-12">
                    {t('chatbot.config.actions.schedule.end_time', { defaultValue: 'Hora fin' })}
                  </label>
                  <input
                    type="time"
                    className="mercately-input"
                    value={action.schedule_end_time || '17:00'}
                    onChange={(e) => handleTimeChange('schedule_end_time', e.target.value, action)}
                  />
                </div>
              </div>
            </div>

            {retailer_info?.timezone && (
              <div className="mt-10">
                <span className="fz-11 tw-text-gray-500">
                  {t('chatbot.config.actions.schedule.timezone_label', { defaultValue: 'Zona horaria del negocio:' })}{' '}
                  <strong>{retailer_info.timezone}</strong>
                </span>
              </div>
            )}
          </div>
        )}

        {action.schedule_error && (
          <div className="mt-10">
            <span className="error-msg tw-text-red-600 fz-12 tw-font-medium">
              {action.schedule_error}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="col-md-12 mt-20">
        <label className="col-form-label fz-14 mr-10">
          ¿Te gustaria ejecutar acciones adicionales para este paso?
        </label>
      </div>
      <div className="col-md-12 d-flex flex-row">
        <div className="d-flex align-items-center mr-10">
          <label className="switch">
            <input
              type="checkbox"
              name="has_step_actions"
              checked={chatbotOption.has_step_actions}
              onChange={handleInputChange}
            />
            <span className="slider round" />
          </label>
        </div>
        <div className="d-flex flex-column fz-14">
          <span className="col-form-label py-0">Acciones avanzadas</span>
          <span className="text-gray-light">Agrega acciones para ejecutar en este paso</span>
        </div>
      </div>

      {errors?.actions_error && (
        <div className="col-md-12 py-10">
          <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-px-3 tw-py-2">
            <span className="error-msg tw-text-red-600 fz-12 tw-font-medium tw-block">
              {errors.actions_error}
            </span>
          </div>
        </div>
      )}

      {chatbotOption.has_step_actions && enabledChatBotActions.map((action, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="mt-20" key={`opt-for-list-${index}`}>
          <div className="col-md-12 mt-20 d-flex justify-content-between">
            <div className="px-0 col-md-11">
              <div className="figma-field mb-0">
                <label>Acción</label>
                <span className="ml-10 error-msg tw-text-red-600 fz-12">{action.action_type_error}</span>
                <Select
                  options={actionTypeOptions}
                  placeholder="Selecciona"
                  className="select-tag"
                  classNamePrefix="select-tag"
                  styles={selectStyles}
                  onChange={(option) => handleSelectAction(option, action)}
                  value={[actionTypeOptions.find((el) => el.value === action.action_type)]}
                  name="action_type"
                  components={{
                    IndicatorSeparator: () => null
                  }}
                />
              </div>
            </div>

            <div
              className="d-flex align-items-center cursor-pointer"
              onClick={() => removeAction(action)}
            >
              <IconMC name="trash-2-outline" size="base" className="tw-text-gray-600" />
            </div>
          </div>

          {action.action_type && renderActionValue(action, action)}
          {allowScheduleConfig && action.action_type && renderScheduleSettings(action)}
        </div>
      ))}

      {chatbotOption.has_step_actions && canAddAction() && (
        <div className="col-md-12 mt-20 px-0 text-center">
          <ButtonMC
            text="Agregar acción"
            icon="plus-outline"
            iconPosition="left"
            variant="SECONDARY"
            outlined
            size="small"
            className="tw-mx-auto tw-w-fit"
            onClick={addNewAction}
          />
        </div>
      )}

      <div className="mb-40" />
    </>
  );
};

export default AdvancedActions;
